<template>
  <Transition name="fade">
    <!-- Eager (not Lazy): the panel must be in the bundle before the first
         hover. As a Nuxt Lazy async component its chunk loaded on demand at
         hover time while the sync <Backdrop> mask painted immediately; a
         force-close in that window mounted the flyout columns into a
         tearing-down subtree, so their onMounted `register-column` emit
         fired on a null
         instance -> "can't access emitsOptions" (ECOM-808, Vue runtime-15).
         Flyout.vue is only a transition wrapper; the heavy columns are
         already eager, so this costs nothing for the Lighthouse goal. -->
    <Flyout
      v-if="shouldDisplay"
      :id="`flyout-${category.id}`"
      :is-open="isOpen"
    >
      <FocusTrap
        :active="isOpen && hasFocusable"
        :escape-deactivates="true"
        :return-focus-on-deactivate="true"
        :click-outside-deactivates="true"
      >
        <div
          :id="`flyout-menu-${category.id}`"
          ref="menuContainer"
          role="menu"
          :aria-label="
            $t('flyout_menu.submenu_label', { categoryName: category.name })
          "
          class="container max-h-[80vh] overflow-auto overscroll-none bg-white shadow-lg"
          tabindex="0"
          @keydown="onKeydown"
        >
          <div
            class="flex pb-12"
            :class="{
              'lg:pt-2 xl:pt-5': true,
            }"
            @mouseleave="handleClose"
            @mouseenter="$emit('mouseenter')"
          >
            <!-- Show Storyblok navigation if available -->
            <FlyoutMenuFirstColumn
              v-show="mainNavigationMatch"
              :main-navigation-match="navigationItem"
              @register-column="registerColumn"
            />

            <!-- Show Storyblok flyout content OR Scayle categories -->
            <FlyoutMenuSecondColumnContent
              v-show="
                hasNavigationFlyoutContent &&
                mainNavigationMatch?.NavigationFlyoutContent
              "
              :content="mainNavigationMatch?.NavigationFlyoutContent || []"
              @register-column="registerColumn"
            />
            <FlyoutMenuSecondColumn
              v-show="shouldShowSecondColumn"
              :has-no-children-at-all="hasNoChildrenAtAll"
              :category-children="category?.children"
              @register-column="registerColumn"
            />

            <!-- Always show clickable images as third column if available -->
            <FlyoutMenuThirdColumn
              v-show="clickableImage.length"
              :clickable-images="clickableImage"
              :has-no-children-at-all="hasNoChildrenAtAll"
              @register-column="registerColumn"
            />
          </div>
        </div>
      </FocusTrap>
    </Flyout>
  </Transition>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { PropType } from 'vue'
import type { Category } from '@scayle/storefront-nuxt'
import { FocusTrap } from 'focus-trap-vue'
import FlyoutMenuFirstColumn from '#layers/base/components/layout/navigations/FlyoutMenuFirstColumn.vue'
import { isHiddenForMainNavigation } from '#layers/base/utils/category'
import type { NavigationItem } from '#layers/base/types/navigation'
import type { SbNavigation } from '~/cms/storyblok/types/storyblok.d.ts'
import {
  findFlyoutNavigationItem,
  hasFlyoutContent,
} from '#layers/base/utils/storyblok'
import { IS_IN_FLYOUT_MENU_KEY } from '#layers/base/constants/injectionKeys'

const props = defineProps({
  isOpen: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  blok: {
    type: Object as PropType<SbNavigation>,
    default: () => {},
  },
  category: {
    type: Object as PropType<Category | NavigationItem>,
    default: () => {},
  },
})

provide(IS_IN_FLYOUT_MENU_KEY, true)

const emit = defineEmits(['mouseenter', 'mouseleave'])
const hasFocusable = ref(false)
const columnRefs = ref<HTMLElement[]>([])
const currentColumn = ref(0)
const currentItem = ref(0)
const menuContainer = ref<HTMLElement | null>(null)
const { lastInteractionType } = useUiState()

const hasNoChildrenAtAll = computed(() => {
  if (!props.category?.children?.length) {
    return true
  }

  // Check if EVERY child is either hidden or has no visible children
  return props.category.children.every((child) => {
    const isHidden = isHiddenForMainNavigation(child)
    const hasNoVisibleChildren = !child.children?.some(
      (grandChild) => !isHiddenForMainNavigation(grandChild),
    )

    return isHidden || hasNoVisibleChildren
  })
})

const mainNavigationMatch = computed(() =>
  findFlyoutNavigationItem(props.blok?.MainNavigation, props.category),
)

const navigationItem = computed(() => {
  // Return the NavigationItem array directly
  return mainNavigationMatch.value?.NavigationItem || []
})

const shouldDisplay = computed(() =>
  hasFlyoutContent(mainNavigationMatch.value, props.category),
)

const hasNavigationFlyoutContent = computed(() => {
  return Boolean(mainNavigationMatch.value?.NavigationFlyoutContent?.length)
})

const clickableImage = computed(() => {
  return mainNavigationMatch.value?.ClickableImage || []
})

const shouldShowSecondColumn = computed(() => {
  if (!mainNavigationMatch.value?.RootCategoryID) {
    return false
  }

  // Check if we have valid children to display
  const hasValidChildren = props.category?.children?.some(
    (c) => !isHiddenForMainNavigation(c),
  )

  return (
    !hasNavigationFlyoutContent.value &&
    hasValidChildren &&
    !!props.category?.children?.length
  )
})

const handleClose = (event: MouseEvent) => {
  emit('mouseleave', event)
}

const checkForFocusable = (): boolean => {
  const el = document.getElementById(`flyout-menu-${props.category?.id}`)
  return !!el?.querySelector(
    'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
  )
}

const registerColumn = (el: HTMLElement | null) => {
  if (el && !columnRefs.value.includes(el)) {
    columnRefs.value.push(el)
  }
}

const getFocusableInColumn = (colIndex: number): HTMLElement[] => {
  const col = columnRefs.value[colIndex]

  if (!col) {
    return []
  }
  return Array.from(
    col.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((el) => el.offsetParent !== null)
}

const setInitialFocus = () => {
  if (lastInteractionType.value === 'keyboard') {
    const focusables = getFocusableInColumn(0)
    focusables[0]?.focus()
  }
}

const onKeydown = (event: KeyboardEvent) => {
  const focusables = getFocusableInColumn(currentColumn.value)
  if (!focusables.length) {
    return
  }

  const activeEl = document.activeElement
  const currentIndex = focusables.findIndex((el) => el === activeEl)

  switch (event.key) {
    case 'Escape':
      emit('mouseleave', event)
      break
    case 'ArrowDown':
      focusables[(currentIndex + 1) % focusables.length]?.focus()
      event.preventDefault()
      break
    case 'ArrowUp':
      focusables[
        (currentIndex - 1 + focusables.length) % focusables.length
      ]?.focus()
      event.preventDefault()
      break
    case 'ArrowRight':
      if (currentColumn.value + 1 < columnRefs.value.length) {
        currentColumn.value++
        getFocusableInColumn(currentColumn.value)[0]?.focus()
        event.preventDefault()
      }
      break
    case 'ArrowLeft':
      if (currentColumn.value > 0) {
        currentColumn.value--
        getFocusableInColumn(currentColumn.value)[0]?.focus()
        event.preventDefault()
      }
      break
  }
}

watch(
  () => props.isOpen,
  async (value) => {
    if (value) {
      currentColumn.value = 0
      currentItem.value = 0
      await nextTick()
      hasFocusable.value = checkForFocusable()
      setInitialFocus()
      menuContainer.value?.focus()
    } else {
      currentColumn.value = 0
      currentItem.value = 0
      hasFocusable.value = false
    }
  },
  { immediate: true },
)
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
